import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Steps, Spin } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Box, Flex, Text } from 'atoms';
import { getProjectListV2 } from 'store/projectSliceV2';
import { searchBankListV2 } from 'store/vendorSliceV2';
import {
	getClientInvoiceDetailV2,
	fetchRegenerationTripsV2,
	buildClientInvoicePreviewV2,
	searchClientPOsV2,
	searchClientWOsV2,
	searchSacCodesV2,
	searchRipplrAddressV2,
	regenerateClientInvoiceV2,
} from 'store/clientInvoiceSliceV2';
import RegenerateInvoiceForm from './components/RegenerateInvoiceForm';
import InvoiceGenerationStep from './components/InvoiceGenerationStep';
import InvoiceReviewModal from './components/InvoiceReviewModal';
import InvoiceSuccessModal from './components/InvoiceSuccessModal';

const { Step } = Steps;

const API_DATE_FMT = 'YYYY-MM-DD';
const SHIPPING_METHOD_LABEL = {
	Road: 'Road',
	Rail: 'Rail',
	Water: 'Water',
	Warehousing: 'Warehouse',
};

const FIXED_ISSUER = {
	value: 'intelligent-retail',
	label: 'Bengaluru, Karnataka',
	legal_name: 'Intelligent Retail Pvt Ltd',
	address:
		'No 413/C, Ground & First Floor, 15th Cross Rd, Muneswara Nagar, ' +
		'6th Sector, HSR Layout, Bengaluru 560102, Karnataka, India',
	gstin: '29AAFCI1726B1ZW',
	pan: 'AAFCI1726B',
	website: 'www.ripplr.in',
	banks: [],
	terms: [
		'Payment has to be cleared as per the payment terms mentioned in the contract.',
		'Please include the Invoice number in your payment notes.',
		'If there is any discrepancy in our invoice, it should be brought to our notice in writing within 7 days from the date of Invoice. Otherwise all charges are deemed correct.',
		'All Cheque/DDs should be drawn in Favour of "Intelligent Retail Private Limited".',
		'Payment to be made in favor of Intelligent Retail Private Limited.',
	],
	signatory: 'ABHISHEK NEHRU',
	contact_email: 'accounts@ripplr.in',
	bank_details: {
		bank_name: 'HDFC Bank',
		account_number: '50200037571741',
		ifsc: 'HDFC0000832',
		account_type: 'Current',
	},
};
const buildRipplrIssuer = (row = {}) => ({
	...row,
	value: row.id ?? row.value,
	terms: Array.isArray(row.terms) && row.terms.length ? row.terms : FIXED_ISSUER.terms,
	website: row.website || FIXED_ISSUER.website,
	signatory: row.signatory || FIXED_ISSUER.signatory,
	contact_email: row.contact_email || FIXED_ISSUER.contact_email,
	bank_details:
		row.bank_details && Object.values(row.bank_details).some(Boolean)
			? row.bank_details
			: FIXED_ISSUER.bank_details,
});
const toAddressId = (v) => {
	const n = Number(v);
	return Number.isInteger(n) && n > 0 ? n : undefined;
};

// Map a PO/WO master list to dropdown options, keyed by the number field.
const toOrderOptions = (rows, key) =>
	(rows || []).map((r) => ({
		value: r[key],
		label: r.valid_till ? `${r[key]} (valid till ${r.valid_till})` : r[key],
	}));

const extractAdditionalCharges = (lineItems) =>
	(lineItems || [])
		.filter((li) => !(Array.isArray(li.trip_ids) && li.trip_ids.length))
		.map((li) => ({
			item_code: li.item_code,
			description: li.description,
			sac: li.sac || undefined,
			amount: li.amount ?? li.rate,
			is_deduction: Boolean(li.is_deduction),
		}));

const PageWrapper = styled(Box)`
	background: #f5f6f8;
	min-height: calc(100vh - 64px);
`;

const StepsCard = styled(Box)`
	background: #fff;
	border-radius: 8px;
	padding: 20px 32px;
	margin: 24px 32px 0;
`;

const StyledSteps = styled(Steps)`
	max-width: 520px;
	margin: 0 auto;

	.ant-steps-item-process .ant-steps-item-icon,
	.ant-steps-item-finish .ant-steps-item-icon {
		background-color: #1e3a5f;
		border-color: #1e3a5f;
	}
	.ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
		color: #fff;
	}
	.ant-steps-item-title {
		font-size: 13px;
	}
`;

const Content = styled(Box)`
	padding: 24px 32px 40px;
`;
const StepPane = styled(Box)`
	display: ${(props) => (props.hidden ? 'none' : 'block')};
`;

const STEPS = [{ title: 'Trips Selection' }, { title: 'Invoice Editing' }];

/**
 * Client invoice regeneration — AR mirror of CreateClientInvoicePageV2, but
 * pre-filled from an existing invoice (route /client-invoices/:id/regenerate
 * and /client-invoices/:id/edit, both routed here). Step 1 reuses the trip
 * table (RegenerateInvoiceForm); Step 2 reuses the rich Create-Invoice
 * generation form, seeded with the old invoice's saved fields and a fresh
 * preview computed from the (possibly changed) trip selection. Submitting
 * raises a regeneration request — same "Regeneration Pending" approval flow
 * as before, just collected in one richer screen instead of two bare ones.
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function RegenerateClientInvoicePageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();

	const [loading, setLoading] = useState(true);
	const [invoice, setInvoice] = useState(null);
	const [trips, setTrips] = useState([]);
	const [projects, setProjects] = useState([]);
	const [banks, setBanks] = useState([]);
	const [bankAccounts, setBankAccounts] = useState([]);
	const [ripplrIssuers, setRipplrIssuers] = useState([]);

	const [invoiceContext, setInvoiceContext] = useState(null);
	const [buildingContext, setBuildingContext] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewPayload, setPreviewPayload] = useState(null);
	const [generating, setGenerating] = useState(false);
	const [successOpen, setSuccessOpen] = useState(false);
	const [successSummary, setSuccessSummary] = useState({});

	const issuers = ripplrIssuers.length ? ripplrIssuers : [FIXED_ISSUER];
	const currentStep = location.state?.wizardStep === 1 && invoiceContext ? 1 : 0;

	useEffect(() => {
		setLoading(true);
		(async () => {
			try {
				const detail = await dispatch(getClientInvoiceDetailV2({ id }));
				const inv = detail?.invoice || null;
				setInvoice(inv);

				const toApiDate = (iso) => (iso ? moment(iso).format(API_DATE_FMT) : undefined);
				const [tripRes, projectRes, bankRes, addressRes] = await Promise.all([
					dispatch(
						fetchRegenerationTripsV2({
							client_invoice_id: id,
							start_date: toApiDate(inv?.bill_start_time),
							end_date: toApiDate(inv?.bill_end_time),
							vehicle_type_ids: inv?.vehicle_type_ids,
							vehicle_numbers: inv?.vehicle_numbers,
						})
					),
					dispatch(
						getProjectListV2({
							filter_by: { is_active: true },
							pagination: { page: 1, per_page: 1000 },
						})
					),
					dispatch(searchBankListV2({ filter_by: {}, limit: 1000 })),
					dispatch(searchRipplrAddressV2({})),
				]);

				setTrips((tripRes?.rows || []).filter((t) => t?.trip_id));
				setProjects(
					(projectRes?.rows || []).map((item) => ({
						value: item?.id,
						label: item?.project_name || '-',
						client_id: item?.client_id,
					}))
				);
				const bankRows = bankRes?.results || [];
				setBankAccounts(bankRows);
				setBanks(bankRows.map((b) => ({ value: b.bank_name, label: b.bank_name })));
				setRipplrIssuers((addressRes || []).map(buildRipplrIssuer));
			} finally {
				setLoading(false);
			}
		})();
	}, [dispatch, id]);

	// Resolve which issuer to prefill Bill-From with: match the old invoice's
	// from_gstin against the live issuer list; fall back to the first issuer
	// (same default behavior as Create Invoice) if no match is found.
	const resolveIssuerValue = (fromGstin, issuerList) => {
		const match = issuerList.find((i) => i.gstin === fromGstin);
		return (match || issuerList[0])?.value;
	};

	const handleProceed = async ({ selectedTrips, reason }) => {
		setBuildingContext(true);
		try {
			// An invoice can bill several projects; the detail response carries the
			// whole set (project_ids) and a joined label, with project_id kept as the
			// primary one for older invoices.
			const invoiceProjectIds = invoice.project_ids?.length
				? invoice.project_ids
				: [invoice.project_id].filter(Boolean);
			const projectLabel =
				invoice.project ||
				invoiceProjectIds
					.map((pid) => projects.find((p) => p.value === pid)?.label)
					.filter(Boolean)
					.join(', ');
			const shippingMethodLabel =
				SHIPPING_METHOD_LABEL[invoice.shipping_method] || invoice.shipping_method;
			const isWarehouseFlow = (invoice.shipping_method || '').toLowerCase() === 'warehousing';

			const pos = await dispatch(
				searchClientPOsV2({ client_id: invoice.client_id, project_ids: invoiceProjectIds })
			);
			const poOptions = toOrderOptions(pos, 'po_number');
			const wos = await dispatch(
				searchClientWOsV2({ client_id: invoice.client_id, project_ids: invoiceProjectIds })
			);
			const woOptions = toOrderOptions(wos, 'wo_number');

			const billFromValue = resolveIssuerValue(invoice.from_gstin, issuers);

			let preview = null;
			if (!isWarehouseFlow) {
				try {
					preview = await dispatch(
						buildClientInvoicePreviewV2({
							req_data: {
								client_id: invoice.client_id,
								project_ids: invoiceProjectIds,
								shipping_method: invoice.shipping_method,
								trip_ids: selectedTrips.map((t) => t.trip_id),
								from_address_id: toAddressId(billFromValue),
								bill_to_gstin: invoice.bill_to_gstin,
								// Exempts this invoice's own (currently billed) trips from the
								// is_client_billed check — they're only "billed" under this invoice.
								client_invoice_id: Number(id),
								groupings: 'by_vehicle',
							},
							setErrors: () => {},
						})
					);
				} catch (e) {
					setBuildingContext(false);
					return;
				}
			}

			const sacCodes = await dispatch(
				searchSacCodesV2({ shipping_method: invoice.shipping_method })
			).catch(() => []);
			const additionalCharges = extractAdditionalCharges(invoice.line_items);

			setInvoiceContext({
				filters: { client_id: invoice.client_id, project_ids: invoiceProjectIds },
				selectedTrips,
				isWarehouseFlow,
				client: invoice.client,
				project: projectLabel,
				shippingMethods: shippingMethodLabel ? [shippingMethodLabel] : [],
				shippingMethod: invoice.shipping_method,
				poOptions,
				woOptions,
				preview,
				clientGstin: invoice.bill_to_gstin || preview?.bill_to_gstin || '',
				clientPan: '',
				billTo: invoice.bill_to_address || '',
				shipTo: invoice.ship_to_address || '',
				issuers,
				sacCodes: sacCodes || [],
				accountTypes: [],
				banks,
				bankAccounts,
				additionalCharges,
				reason,
				// Fields InvoiceGenerationStep seeds `meta`/billTo/shipTo from on mount.
				prefill: {
					bill_from: billFromValue,
					due_date: invoice.due_date,
					po_number: invoice.po_number,
					wo_number: invoice.wo_number,
					bank: invoice.bank_details?.bank_name,
					account_number: invoice.bank_details?.account_number,
					ifsc: invoice.bank_details?.ifsc,
					account_type: invoice.bank_details?.account_type,
					tax_mode: invoice.tax_mode,
					remarks: invoice.remarks,
					bill_to_address: invoice.bill_to_address,
					ship_to_address: invoice.ship_to_address,
				},
			});
			navigate(location.pathname, { state: { wizardStep: 1 } });
		} finally {
			setBuildingContext(false);
		}
	};

	const handleGenerate = (invoicePayload) => {
		setPreviewPayload(invoicePayload);
		setPreviewOpen(true);
	};

	const handleConfirmRegenerate = async () => {
		const invoicePayload = previewPayload || {};
		const { meta = {}, billTo, shipTo, lineItems = [], clientGstin, taxMode } = invoicePayload;
		const line_items = lineItems
			.filter((r) => !r.is_additional)
			.map((r) => ({
				item_code: r.item,
				description: r.description,
				sac: r.sac_code,
				qty: r.qty,
				rate: r.rate,
				trip_ids: r.trip_ids || [],
			}));
		// Deductions are discounts on the supply, so they travel as negative-amount
		// charge lines and the backend nets them out of their own slab's taxable value.
		// They are deliberately no longer folded into customer_debit_amount, which is a
		// post-tax settlement field (see the payment rollup) and cannot move the GST base.
		const additional_charges = lineItems
			.filter((r) => r.is_additional)
			.map((r) => ({
				description: r.description,
				sac: r.sac_code,
				amount: r.amount,
				is_deduction: Boolean(r.is_deduction),
			}));

		const req_data = {
			client_invoice_id: Number(id),
			trip_ids: (invoiceContext?.selectedTrips || []).map((t) => t.trip_id),
			reason: invoiceContext?.reason,
			project_ids: invoiceContext?.filters?.project_ids,
			shipping_method: invoiceContext?.shippingMethod,
			due_date: meta.due_date ? moment(meta.due_date).format(API_DATE_FMT) : undefined,
			po_number: meta.po_number,
			wo_number: meta.wo_number,
			from_address_id: toAddressId(meta.bill_from),
			bill_to_gstin: (clientGstin ?? invoiceContext?.clientGstin ?? '').trim() || undefined,
			tax_mode: taxMode,
			bill_to_address: billTo,
			ship_to_address: shipTo,
			line_items,
			additional_charges,
			bank_details: {
				bank_name: meta.bank,
				account_number: meta.account_number,
				ifsc: meta.ifsc,
				account_type: meta.account_type,
			},
			remarks: meta.remarks,
		};

		setGenerating(true);
		const res = await dispatch(
			regenerateClientInvoiceV2({ req_data, setErrors: () => {} })
		).catch(() => null);
		setGenerating(false);
		if (res?.status !== 'success') return;

		setSuccessSummary({
			client: invoiceContext?.client,
			project: invoiceContext?.project,
			invoiceNo: res?.data?.new_invoice_no,
			invoiceId: res?.data?.new_invoice_id,
			total: invoicePayload?.totals?.total,
			dueDate: meta.due_date ? moment(meta.due_date).format(API_DATE_FMT) : '-',
		});
		setPreviewOpen(false);
		setSuccessOpen(true);
	};

	const handleDownloadSuccess = async () => {
		if (!successSummary.invoiceId) return;
		const detail = await dispatch(
			getClientInvoiceDetailV2({ id: successSummary.invoiceId })
		).catch(() => null);
		const url = detail?.invoice?.pdf_url;
		if (url) window.open(url);
	};

	return (
		<PageWrapper>
			<Flex
				alignItems="center"
				style={{
					background: '#fff',
					padding: '16px 24px',
					borderBottom: '1px solid #f0f0f0',
					gap: 12,
				}}
			>
				<ArrowLeftOutlined
					style={{ cursor: 'pointer' }}
					onClick={() => (currentStep > 0 ? navigate(-1) : navigate('/client-invoices'))}
				/>
				<Text style={{ fontSize: 18, fontWeight: 600 }}>
					Client Invoice Editing{invoice?.invoice_no ? ` : ${invoice.invoice_no}` : ''}
				</Text>
			</Flex>

			<StepsCard>
				<StyledSteps current={currentStep} labelPlacement="vertical">
					{STEPS.map((s) => (
						<Step key={s.title} title={s.title} />
					))}
				</StyledSteps>
			</StepsCard>

			<Spin spinning={loading || buildingContext}>
				<Content>
					{invoice && (
						<StepPane hidden={currentStep !== 0}>
							<RegenerateInvoiceForm
								invoice={invoice}
								trips={trips}
								onProceed={handleProceed}
							/>
						</StepPane>
					)}
					{invoice && currentStep === 1 && (
						<InvoiceGenerationStep
							// Remount once prefill data is actually ready so the internal
							// useState seeds pick up the loaded invoice/preview values.
							key={invoiceContext ? `regen-${id}` : 'regen-pending'}
							context={invoiceContext}
							onBack={() => navigate(-1)}
							onGenerate={handleGenerate}
							generateLabel="Regenerate"
						/>
					)}
				</Content>
			</Spin>

			<InvoiceReviewModal
				visible={previewOpen}
				context={invoiceContext}
				payload={previewPayload}
				loading={generating}
				onBack={() => setPreviewOpen(false)}
				onConfirm={handleConfirmRegenerate}
				confirmLabel="Regenerate"
			/>

			<InvoiceSuccessModal
				visible={successOpen}
				summary={successSummary}
				title="Regeneration Request Submitted — Pending Approval"
				onDownload={handleDownloadSuccess}
				onContinue={() => {
					setSuccessOpen(false);
					navigate('/client-invoices');
				}}
			/>
		</PageWrapper>
	);
}

export default RegenerateClientInvoicePageV2;
